import React, { Fragment } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Disclosure, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

const Navbar = () => {
  const { currentUser, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  if (!currentUser) return null;

  const navigation = [
    { name: "User Order", href: "/" },
    { name: "Cashier View", href: "/cashier" },
    { name: "Sessions", href: "/sessions" },
    { name: "Sales Reports", href: "/reports" },
    { name: "📊 Analytics", href: "/analytics" },
    { name: "Manage Products", href: "/products" },
    { name: "Inventory", href: "/inventory" },
    { name: "⚙️ Settings", href: "/settings" },
  ];

  if (userRole === "admin") {
    navigation.push({ name: "User Roles", href: "/user-roles", special: true });
  }

  return (
    <Disclosure
      as="nav"
      className="sticky top-0 z-50 bg-gradient-navbar backdrop-blur-xl shadow-large border-b border-white/10"
    >
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-20 items-center justify-between">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <img
                  src="/logo192.png"
                  alt="POS System"
                  className="h-16 w-auto transition-transform duration-300 hover:scale-105 hover:rotate-2 drop-shadow-lg rounded-lg"
                />
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex flex-1 items-center justify-center">
                <div className="flex items-center gap-1 bg-white/5 px-3 py-2 rounded-xl border border-white/10 backdrop-blur-sm overflow-x-auto">
                  {navigation.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                          px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300
                          ${
                            active
                              ? "bg-white/20 text-white font-semibold shadow-md border border-white/30"
                              : "text-white/90 hover:bg-white/10 hover:text-white hover:border hover:border-white/20"
                          }
                          ${
                            item.special
                              ? "bg-gradient-danger text-white font-semibold shadow-danger"
                              : ""
                          }
                          whitespace-nowrap
                        `}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Desktop User Actions */}
              <div className="hidden md:flex items-center gap-3">
                <span className="text-sm font-medium text-white/90 px-4 py-2.5 bg-white/10 rounded-lg border border-white/15 backdrop-blur-sm hover:bg-white/15 transition-all">
                  {currentUser.email}
                </span>
                <button
                  onClick={handleLogout}
                  className="px-6 py-2.5 bg-gradient-danger text-white font-semibold rounded-lg shadow-danger hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 border border-white/20"
                >
                  Logout
                </button>
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-lg bg-white/10 text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/50 transition-all">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <Transition
            enter="transition duration-200 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-100 ease-in"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel className="md:hidden">
              <div className="space-y-1 px-4 pb-3 pt-2 bg-slate-800/95 backdrop-blur-sm border-t border-white/10">
                <div className="mb-3 text-xs font-semibold text-white/60 uppercase tracking-wider border-b border-white/10 pb-2">
                  Menu
                </div>
                {navigation.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Disclosure.Button
                      key={item.name}
                      as={Link}
                      to={item.href}
                      className={`
                        block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200
                        border-l-3
                        ${
                          active
                            ? "bg-white/15 text-white font-semibold border-l-primary-500 pl-5"
                            : "text-white/90 border-l-transparent hover:bg-white/10 hover:border-l-white/40 hover:pl-5"
                        }
                        ${
                          item.special
                            ? "bg-gradient-danger text-white border-l-danger-600"
                            : ""
                        }
                      `}
                    >
                      {item.name}
                    </Disclosure.Button>
                  );
                })}

                {/* Mobile User Info */}
                <div className="mt-6 pt-4 border-t border-white/10 bg-black/20 rounded-lg p-4">
                  <div className="mb-3 px-4 py-3 bg-white/5 rounded-lg border border-white/10">
                    <p className="text-sm text-white/80 font-medium break-all">
                      {currentUser.email}
                    </p>
                  </div>
                  <Disclosure.Button
                    as="button"
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-danger text-white font-semibold rounded-lg shadow-danger hover:shadow-lg transition-all"
                  >
                    <span>→</span>
                    Logout
                  </Disclosure.Button>
                </div>
              </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};

export default Navbar;
